import type { VisualElement } from "@judo/model-api";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, AlertTitle, Box, Collapse, IconButton, Typography } from "@mui/material";
import { Component, type ErrorInfo, type ReactNode } from "react";

export interface ContainerErrorBoundaryProps {
	children: ReactNode;
	containerType: "TABLE" | "FORM" | "VIEW";
	element?: VisualElement;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
	showDetails: boolean;
}

/**
 * Error boundary for container types.
 * No retry - displays error with model context for debugging.
 */
export class ContainerErrorBoundary extends Component<ContainerErrorBoundaryProps, ErrorBoundaryState> {
	state: ErrorBoundaryState = {
		hasError: false,
		error: null,
		errorInfo: null,
		showDetails: false,
	};

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		this.setState({ errorInfo });
		// Log to error reporting service
		console.error("Container render error:", error, errorInfo);
	}

	toggleDetails = (): void => {
		this.setState((prev) => ({ showDetails: !prev.showDetails }));
	};

	/**
	 * Safely stringify objects with cyclic references.
	 */
	private safeStringify(obj: unknown): string {
		const seen = new WeakSet();
		return JSON.stringify(
			obj,
			(key, value) => {
				if (typeof value === "object" && value !== null) {
					if (seen.has(value)) {
						return "[Circular]";
					}
					seen.add(value);
				}
				return value;
			},
			2
		);
	}

	render() {
		if (this.state.hasError) {
			const { containerType, element } = this.props;
			const { error, errorInfo, showDetails } = this.state;

			return (
				<Box p={2} data-testid={`error-boundary::${containerType.toLowerCase()}`}>
					<Alert severity="error">
						<AlertTitle>{containerType} Container Error</AlertTitle>
						<Typography variant="body2">
							Failed to render {containerType.toLowerCase()} container
							{element && ` for element: ${element.name}`}
						</Typography>

						<Box mt={1}>
							<IconButton
								size="small"
								onClick={this.toggleDetails}
								data-testid="error-boundary::toggle-details"
								aria-label={showDetails ? "Hide details" : "Show details"}
							>
								{showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
							</IconButton>
							<Typography variant="caption" component="span">
								{showDetails ? "Hide" : "Show"} Details
							</Typography>
						</Box>

						<Collapse in={showDetails}>
							<Box mt={2} p={1} sx={{ bgcolor: "grey.100", borderRadius: 1 }} data-testid="error-boundary::details">
								<Typography variant="subtitle2">Error:</Typography>
								<Typography variant="body2" fontFamily="monospace" data-testid="error-boundary::error-message">
									{error?.message}
								</Typography>

								{element && (
									<>
										<Typography variant="subtitle2" mt={1}>
											Element Path:
										</Typography>
										<Typography variant="body2" fontFamily="monospace" data-testid="error-boundary::element-path">
											{element.sourceId ?? element["xmi:id"]}
										</Typography>

										<Typography variant="subtitle2" mt={1}>
											Model Snippet:
										</Typography>
										<Box
											component="pre"
											fontSize="0.75rem"
											overflow="auto"
											maxHeight={200}
											data-testid="error-boundary::model-snippet"
										>
											{this.safeStringify(element)}
										</Box>
									</>
								)}

								{errorInfo && (
									<>
										<Typography variant="subtitle2" mt={1}>
											Component Stack:
										</Typography>
										<Box
											component="pre"
											fontSize="0.75rem"
											overflow="auto"
											maxHeight={200}
											data-testid="error-boundary::component-stack"
										>
											{errorInfo.componentStack}
										</Box>
									</>
								)}
							</Box>
						</Collapse>
					</Alert>
				</Box>
			);
		}

		return this.props.children;
	}
}

// Specific error boundaries per container type
export function TableContainerErrorBoundary({ children, element }: { children: ReactNode; element?: VisualElement }) {
	return (
		<ContainerErrorBoundary containerType="TABLE" element={element}>
			{children}
		</ContainerErrorBoundary>
	);
}

export function FormContainerErrorBoundary({ children, element }: { children: ReactNode; element?: VisualElement }) {
	return (
		<ContainerErrorBoundary containerType="FORM" element={element}>
			{children}
		</ContainerErrorBoundary>
	);
}

export function ViewContainerErrorBoundary({ children, element }: { children: ReactNode; element?: VisualElement }) {
	return (
		<ContainerErrorBoundary containerType="VIEW" element={element}>
			{children}
		</ContainerErrorBoundary>
	);
}
